import { DatePipe } from '@angular/common';
import { Component, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import _ from 'lodash';
import moment from 'moment-timezone';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';

import { NotificationService } from 'src/app/notification/services/notification.service';
import { AbstractPageForm } from 'src/app/shared/pages/page-form.abstract';
import { AuctionComponent } from '../../../auction/auction/auction.component';
import { GraphqlService } from '../../../graphql/graphql.service';
import { Contract, Holiday, MarginOption, Product, RfqSession, ServiceTypeUnit, Supplier } from '../../../shared/models';

@Component({
	selector: 'pk-broker-contract-auction',
	templateUrl: './contract-auction.component.html',
	styleUrls: ['./contract-auction.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ContractAuctionModalComponent extends AbstractPageForm {
	@ViewChild(AuctionComponent, { static: false }) auction: AuctionComponent;

	public warnings: string[];
	public onSuccess = new Subject<RfqSession>();
	public onExtend = new Subject<RfqSession>();
	public generatedLoa = new Subject<boolean>();
	public uploadedLoa = new Subject<boolean>();

	public contractId: string;
	public contract: Contract;
	public products: Product[];
	public suppliers: Supplier[];
	public otherSuppliers: Supplier[];
	public holidays: Holiday[];
	public defaultProducts: Product[] = [];
	public serviceTypeUnit: ServiceTypeUnit;
	public marginOptions: MarginOption[];

	public auctionView: 'auction' | 'help' = 'auction';
	public acceptedLoadFactorRecommendation = false;

	constructor(
		public activeModal: BsModalRef,
		private notificationService: NotificationService,
		private datePipe: DatePipe,
		private graphqlService: GraphqlService,
	) {
		super();
	}

	public async init(contractId: string): Promise<void> {
		this.contractId = contractId;
		this.loading = true;
		this.warnings = [];

		try {
			const dateGTE = this.datePipe.transform(new Date(), 'yyyy-MM-ddT00:00:00');

			const contractResult = await this.graphqlService.getAuctionModalContractData(this.contractId);
			this.contract = new Contract(contractResult.data.contract);
			const formResult = await this.graphqlService.getAuctionModalFormData(
				this.contract.serviceTypeId,
				this.contract.stateId,
				this.contract.getUtilityId(),
				this.contract.agentId,
				this.contract.annualUsage,
				this.contract.supplierId,
				moment(this.contract.effectiveDate).format('YYYY-MM-DD'),
				dateGTE,
			);
			this.products = formResult.data.products.message.map(p => new Product(p));
			this.holidays = formResult.data.holidays.message.map(h => new Holiday(h));
			this.serviceTypeUnit = formResult.data.serviceTypeUnit;
			this.marginOptions = formResult.data.agentMarginOptions;
			this.suppliers = _.orderBy(
				formResult.data.availableSuppliers.message.map(s => new Supplier(s)),
				s => s.name,
			);
			const supplierIds = this.suppliers.map(s => s.supplierId);
			const otherSuppliers = formResult.data.otherSuppliers.message.map(s => new Supplier(s)).filter(s => !supplierIds.includes(s.supplierId));
			this.otherSuppliers = _.orderBy(otherSuppliers, s => s.name);
			if (this.contract.productId) {
				this.defaultProducts.push({
					productId: this.contract.productId,
				} as Product);
			} else {
				this.defaultProducts.push(this.getDefaultProduct());
			}
		} catch (e) {
			this.warnings = [this.getErrorMessage('loading this form')];
		} finally {
			this.loading = false;
		}
	}

	private getDefaultProduct(): Product {
		let defaultProduct = this.products.find(p =>
			[this.CONSTANTS.products.fixedAllInElectric, this.CONSTANTS.products.fixedAllInGas].includes(p.productId),
		);
		if (!defaultProduct) {
			defaultProduct = this.products[0];
		}

		return defaultProduct;
	}

	get agentIsOnHold(): boolean {
		return this.loggedInUser.agent?.holdEnrollments;
	}

	get showLoadFactorRecommendation(): boolean {
		return (
			!this.loading &&
			this.contract &&
			this.contract.loadFactor &&
			this.contract.loadFactor < 50 &&
			!this.acceptedLoadFactorRecommendation &&
			this.contract.hasUsage
		);
	}

	public acceptLoadFactorRecommendation(): void {
		this.acceptedLoadFactorRecommendation = !this.acceptedLoadFactorRecommendation;
		this.refreshView();
	}

	public onUpdateAuctionView(auctionView: 'auction' | 'help'): void {
		this.auctionView = auctionView;
		this.refreshView();
	}

	public async onAuctionCreateCompleted(rfqSession: RfqSession): Promise<void> {
		this.onSuccess.next(rfqSession);
		this.activeModal.hide();
		const body =
			`Your pricing session was successfully started. Your pricing session is set to end on ` +
			`${rfqSession.formatEndDate()} CT. Since pricing is only valid until close of business the ` +
			`day it's provided, suppliers typically provide pricing the day a pricing session ends to ensure ` +
			`it's valid. Check back for pricing at that time.`;

		this.notificationService.info({ initialState: { title: 'Start Pricing Session', body }, confirmationType: 'ok' });
	}
}
